import React, { useState } from "react";
import { Box, Button, TextField, Select, MenuItem, FormControl, FormControlLabel, InputLabel, Typography, Checkbox } from "@mui/material";
import { Formik, FieldArray } from "formik";
import * as yup from "yup";
import { useNavigate } from 'react-router-dom';
import { useAddNewDataAssetMutation } from '../../../features/data_assets/dataAssetsApiSlice';

const initialValues = {
    name: "",
    usage: "",
    assetType: "",
    s3_uris: [],
    snowflake_tables: [],
    updateCadence: {
        realTimeRefreshRate: null,
    },
    versions: [],
    countsStats: [],
    isRebuilt: false,
    rebuild: {
        frequency: "",
        time: "",
        day: null,
        dayOfWeek: "",
        cronSchedule: "",
    },
};
const formatNumberWithCommas = (x) => {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const dataAssetSchema = yup.object().shape({
    name: yup.string().required("Required"),
    usage: yup.string().required("Required"),
    assetType: yup.string().required("Required"),
    // ...add validations for other fields as necessary
});

const DataAssetForm = () => {
    const navigate = useNavigate();
    const [addNewDataAsset] = useAddNewDataAssetMutation();

    const handleFormSubmit = async (values) => {
        try {
            // Format the versions array to include the concatenated version string
            const formattedVersions = values.versions.map((version) => {
                return {
                    ...version,
                    version: `${version.major}.${version.minor}.${version.patch}`,
                };
            });

            // Format the countsStats array to convert the value back to a number
            const formattedCountsStats = values.countsStats.map((countStat) => {
                return {
                    ...countStat,
                    value: parseInt(countStat.value.replace(/,/g, ''), 10),
                };
            });

            // Create a new object with formatted versions array
            const formValues = {
                ...values,
                versions: formattedVersions,
                countsStats: formattedCountsStats,
            };

            // Pass the formValues object when making the API call
            await addNewDataAsset(formValues);
            console.log("Data Asset saved successfully");
            navigate('/console/data');
        } catch (error) {
            console.error("Error saving Data Asset:", error);
        }
    };

    return (
        <Box m="20px">
            <Typography variant="h4">Create Data Asset</Typography>
            <Formik
                onSubmit={handleFormSubmit}
                initialValues={initialValues}
                validationSchema={dataAssetSchema}
            >
                {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
                    <form onSubmit={handleSubmit}>
                        <Box display="grid" gap="30px">
                            <TextField
                                fullWidth
                                variant="filled"
                                type="text"
                                label="Name"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.name}
                                name="name"
                                error={!!touched.name && !!errors.name}
                                helperText={touched.name && errors.name}
                            />

                            <FormControl fullWidth variant="filled">
                                <InputLabel htmlFor="usage">Usage</InputLabel>
                                <Select
                                    id="usage"
                                    name="usage"
                                    value={values.usage}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    label="Usage"
                                >
                                    <MenuItem value="Internal">Internal</MenuItem>
                                    <MenuItem value="External">External</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth variant="filled">
                                <InputLabel htmlFor="assetType">Asset Type</InputLabel>
                                <Select
                                    id="assetType"
                                    name="assetType"
                                    value={values.assetType}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    label="Asset Type"
                                >
                                    <MenuItem value="Real-Time">Real-Time</MenuItem>
                                    <MenuItem value="Standard Delivery">Standard Delivery</MenuItem>
                                </Select>
                            </FormControl>

                            {values.assetType === "Real-Time" && (
                                <Box>
                                    <Typography variant="h6">S3 URIs</Typography>
                                    <FieldArray name="s3_uris">
                                        {({ push, remove }) => (
                                            <div>
                                                {values.s3_uris.map((_, index) => (
                                                    <Box key={index} display="flex" alignItems="center" mb={2}>
                                                        <Box mr={2} flexBasis="50%">
                                                            <TextField
                                                                fullWidth
                                                                variant="filled"
                                                                type="text"
                                                                label="S3 URI"
                                                                onBlur={handleBlur}
                                                                onChange={(e) => setFieldValue(`s3_uris.${index}.uri`, e.target.value)}
                                                                value={values.s3_uris[index].uri}
                                                                name={`s3_uris.${index}.uri`}
                                                            />
                                                        </Box>
                                                        <Box mr={2} flexBasis="50%">
                                                            <TextField
                                                                fullWidth
                                                                variant="filled"
                                                                type="text"
                                                                label="Description"
                                                                onBlur={handleBlur}
                                                                onChange={(e) => setFieldValue(`s3_uris.${index}.description`, e.target.value)}
                                                                value={values.s3_uris[index].description}
                                                                name={`s3_uris.${index}.description`}
                                                            />
                                                        </Box>
                                                        <Button variant="outlined" color="secondary" onClick={() => remove(index)}>Delete</Button>
                                                    </Box>
                                                ))}
                                                <Button variant="outlined" color="secondary" onClick={() => push({ uri: "", description: "" })}>
                                                    <Box component="span" fontSize="20px">+</Box> Add S3 URI
                                                </Button>
                                            </div>
                                        )}
                                    </FieldArray>

                                    <Typography variant="h6">Snowflake Table Locations</Typography>
                                    <FieldArray name="snowflake_tables">
                                        {({ push, remove }) => (
                                            <div>
                                                {values.snowflake_tables.map((_, index) => (
                                                    <Box key={index} display="flex" alignItems="center" mb={2}>
                                                        <Box mr={2} flexBasis="50%">
                                                            <TextField
                                                                fullWidth
                                                                variant="filled"
                                                                type="text"
                                                                label="Snowflake Table"
                                                                onBlur={handleBlur}
                                                                onChange={(e) => setFieldValue(`snowflake_tables.${index}.table`, e.target.value)}
                                                                value={values.snowflake_tables[index].table}
                                                                name={`snowflake_tables.${index}.table`}
                                                            />
                                                        </Box>
                                                        <Box mr={2} flexBasis="50%">
                                                            <TextField
                                                                fullWidth
                                                                variant="filled"
                                                                type="text"
                                                                label="Description"
                                                                onBlur={handleBlur}
                                                                onChange={(e) => setFieldValue(`snowflake_tables.${index}.description`, e.target.value)}
                                                                value={values.snowflake_tables[index].description}
                                                                name={`snowflake_tables.${index}.description`}
                                                            />
                                                        </Box>
                                                        <Button variant="outlined" color="secondary" onClick={() => remove(index)}>Delete</Button>
                                                    </Box>
                                                ))}
                                                <Button variant="outlined" color="secondary" onClick={() => push({ table: "", description: "" })}>
                                                    <Box component="span" fontSize="20px">+</Box> Add Snowflake Table
                                                </Button>
                                            </div>
                                        )}
                                    </FieldArray>
                                    <Box mt={3}>
                                        <Typography variant="h6">Real Time Refresh Rate (Minutes)</Typography>
                                        <TextField
                                            fullWidth
                                            style={{ width: '10%' }}
                                            select
                                            label="Refresh Rate (Minutes)"
                                            value={values.updateCadence.realTimeRefreshRate}
                                            onChange={(e) => setFieldValue('updateCadence.realTimeRefreshRate', e.target.value)}
                                            name="realTimeRefreshRate"
                                        >
                                            {[1, 5, 10, 15].map((minute) => (
                                            <MenuItem key={minute} value={minute}>
                                                {minute}
                                            </MenuItem>
                                            ))}
                                        </TextField>
                                        </Box>
                                    </Box>
                                    )}
                            {values.assetType === "Standard Delivery" && (
                                <Box>
                                    <Typography variant="h6">Versions</Typography>
                                        <FieldArray name="versions">
                                            {({ push, remove }) => (
                                                <div>
                                                    {values.versions.map((_, index) => (
                                                        <Box key={index} display="flex" alignItems="center" mb={2}>
                                                            <Box display="flex" alignItems="center" mr={2} flexBasis="25%">
                                                                <TextField
                                                                    variant="filled"
                                                                    type="number"
                                                                    label="Major"
                                                                    onBlur={handleBlur}
                                                                    onChange={(e) => setFieldValue(`versions.${index}.major`, e.target.value)}
                                                                    value={values.versions[index].major}
                                                                    name={`versions.${index}.major`}
                                                                    inputProps={{ min: 0, style: { width: '50px' } }}
                                                                />
                                                                <Box px={1}>.</Box>
                                                                <TextField
                                                                    variant="filled"
                                                                    type="number"
                                                                    label="Minor"
                                                                    onBlur={handleBlur}
                                                                    onChange={(e) => setFieldValue(`versions.${index}.minor`, e.target.value)}
                                                                    value={values.versions[index].minor}
                                                                    name={`versions.${index}.minor`}
                                                                    inputProps={{ min: 0, style: { width: '50px' } }}
                                                                />
                                                                <Box px={1}>.</Box>
                                                                <TextField
                                                                    variant="filled"
                                                                    type="number"
                                                                    label="Patch"
                                                                    onBlur={handleBlur}
                                                                    onChange={(e) => setFieldValue(`versions.${index}.patch`, e.target.value)}
                                                                    value={values.versions[index].patch}
                                                                    name={`versions.${index}.patch`}
                                                                    inputProps={{ min: 0, style: { width: '50px' } }}
                                                                />
                                                            </Box>
                                                            <Box mr={2} flexBasis="25%">
                                                                <TextField
                                                                    fullWidth
                                                                    select
                                                                    label="Source"
                                                                    value={values.versions[index].source}
                                                                    onChange={(e) => setFieldValue(`versions.${index}.source`, e.target.value)}
                                                                    name={`versions.${index}.source`}
                                                                    variant="filled"
                                                                >
                                                                    <MenuItem value="Snowflake">Snowflake</MenuItem>
                                                                    <MenuItem value="S3">S3</MenuItem>
                                                                    <MenuItem value="Both">Both</MenuItem>
                                                                </TextField>
                                                            </Box>
                                                            {(values.versions[index].source === "Snowflake" || values.versions[index].source === "Both") && (
                                                                <Box mr={2} flexBasis="25%">
                                                                    <TextField
                                                                        fullWidth
                                                                        variant="filled"
                                                                        type="text"
                                                                        label="Snowflake Table"
                                                                        onBlur={handleBlur}
                                                                        onChange={(e) => setFieldValue(`versions.${index}.snowflake_table`, e.target.value)}
                                                                        value={values.versions[index].snowflake_table}
                                                                        name={`versions.${index}.snowflake_table`}
                                                                    />
                                                                </Box>
                                                            )}
                                                            {(values.versions[index].source === "S3" || values.versions[index].source === "Both") && (
                                                                <Box mr={2} flexBasis="25%">
                                                                    <TextField
                                                                        fullWidth
                                                                        variant="filled"
                                                                        type="text"
                                                                        label="S3 URI"
                                                                        onBlur={handleBlur}
                                                                        onChange={(e) => setFieldValue(`versions.${index}.s3_uri`, e.target.value)}
                                                                        value={values.versions[index].s3_uri}
                                                                        name={`versions.${index}.s3_uri`}
                                                                    />
                                                                </Box>
                                                            )}
                                                            <Button variant="outlined" color="secondary" onClick={() => remove(index)}>Delete</Button>
                                                        </Box>
                                                    ))}
                                                    <Button variant="outlined" color="secondary" onClick={() => push({ version: "", source: "", s3_uri: "", snowflake_table: "" })}>
                                                        <Box component="span" fontSize="20px">+</Box> Add Version
                                                    </Button>
                                                </div>
                                            )}
                                        </FieldArray>

                                        <Typography variant="h6">Table Rebuild</Typography>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.rebuild.isRebuilt}
                                                    onChange={handleChange}
                                                    name="rebuild.isRebuilt"
                                                    color="secondary"
                                                />
                                            }
                                            label="Is the table rebuilt?"
                                        />

                                        {values.rebuild.isRebuilt && (
                                            <Box>
                                                <TextField
                                                    style={{ width: '50%' }}
                                                    select
                                                    label="Rebuild Frequency"
                                                    value={values.rebuild?.frequency}
                                                    onChange={(e) => setFieldValue("rebuild.frequency", e.target.value)}
                                                    name="rebuild.frequency"
                                                    variant="filled"
                                                >
                                                    <MenuItem value="daily">Daily</MenuItem>
                                                    <MenuItem value="weekly">Weekly</MenuItem>
                                                    <MenuItem value="monthly">Monthly</MenuItem>
                                                    <MenuItem value="cron">CRON Schedule</MenuItem>
                                                </TextField>

                                                {values.rebuild?.frequency === "daily" && (
                                                    <TextField
                                                        label="Rebuild Time"
                                                        type="time"
                                                        name="rebuild.time"
                                                        value={values.rebuild?.time}
                                                        onChange={(e) => setFieldValue("rebuild.time", e.target.value)}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                        inputProps={{
                                                            step: 300, // 5 min
                                                        }}
                                                        variant="filled"
                                                    />
                                                )}

                                                {values.rebuild.frequency === "weekly" && (
                                                    <Box display="flex" alignItems="center" justifyContent="left">
                                                        <TextField
                                                            style={{ width: '25%' }}
                                                            select
                                                            label="Rebuild Day of Week"
                                                            value={values.rebuild.dayOfWeek}
                                                            onChange={(e) => setFieldValue("rebuild.dayOfWeek", e.target.value)}
                                                            name="rebuild.dayOfWeek"
                                                            variant="filled"
                                                        >
                                                            <MenuItem value="Monday">Monday</MenuItem>
                                                            <MenuItem value="Tuesday">Tuesday</MenuItem>
                                                            <MenuItem value="Wednesday">Wednesday</MenuItem>
                                                            <MenuItem value="Thursday">Thursday</MenuItem>
                                                            <MenuItem value="Friday">Friday</MenuItem>
                                                            <MenuItem value="Saturday">Saturday</MenuItem>
                                                            <MenuItem value="Sunday">Sunday</MenuItem>
                                                        </TextField>
                                                        <Box mx={2}>
                                                            <TextField
                                                                label="Rebuild Time"
                                                                style={{ width: '100%' }}
                                                                type="time"
                                                                name="rebuild.time"
                                                                value={values.rebuild.time}
                                                                onChange={(e) => setFieldValue("rebuild.time", e.target.value)}
                                                                InputLabelProps={{
                                                                    shrink: true,
                                                                }}
                                                                inputProps={{
                                                                    step: 300, // 5 min
                                                                }}
                                                                variant="filled"
                                                            />
                                                        </Box>
                                                    </Box>
                                                )}

                                                {values.rebuild.frequency === "monthly" && (
                                                    <Box display="flex" alignItems="center" justifyContent="left">
                                                        <FormControl variant="filled">
                                                            <InputLabel htmlFor="rebuild-day">Rebuild Day of Month</InputLabel>
                                                            <Select
                                                                labelId="rebuild-day-label"
                                                                id="rebuild-day"
                                                                name="rebuild.day"
                                                                value={values.rebuild.day}
                                                                onChange={(e) => setFieldValue("rebuild.day", e.target.value)}
                                                                label="Rebuild Day of Month"
                                                            >
                                                                {[...Array(31)].map((_, index) => (
                                                                    <MenuItem key={index} value={index + 1}>
                                                                        {index + 1}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        <Box mx={2}>
                                                        <TextField
                                                                label="Rebuild Time"
                                                                type="time"
                                                                name="rebuild.time"
                                                                value={values.rebuild.time}
                                                                onChange={(e) => setFieldValue("rebuild.time", e.target.value)}
                                                                InputLabelProps={{
                                                                    shrink: true,
                                                                }}
                                                                inputProps={{
                                                                    step: 300, // 5 min
                                                                }}
                                                                variant="filled"
                                                            />
                                                        </Box>
                                                    </Box>
                                                )}

                                                {values.rebuild.frequency === "cron" && (
                                                    <TextField
                                                        style={{ width: '25%' }}
                                                        label="CRON Expression"
                                                        type="text"
                                                        name="rebuild.cronSchedule"
                                                        value={values.rebuild.cronSchedule}
                                                        onChange={(e) => setFieldValue("rebuild.cronSchedule", e.target.value)}
                                                        variant="filled"
                                                    />
                                                )}
                                            </Box>
                                        )}

                       

                                </Box>
                            )}
                            <Box mt={3}>
                            <Typography variant="h6">Counts/Stats</Typography>
                            <FieldArray name="countsStats">
                                {({ push, remove }) => (
                                <div>
                                    {values.countsStats.map((_, index) => (
                                    <Box key={index} display="flex" alignItems="center" mb={2}>
                                        <Box mr={2} flexBasis="33%">
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="Attribute Name"
                                            onBlur={handleBlur}
                                            onChange={(e) => setFieldValue(`countsStats.${index}.name`, e.target.value)}
                                            value={values.countsStats[index].name}
                                            name={`countsStats.${index}.name`}
                                        />
                                        </Box>
                                        <Box mr={2} flexBasis="33%">
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="Initial Value"
                                            onBlur={handleBlur}
                                            onChange={(e) => {
                                                const formattedValue = formatNumberWithCommas(e.target.value.replace(/,/g, ''));
                                                setFieldValue(`countsStats.${index}.value`, formattedValue);
                                            }}
                                            value={values.countsStats[index].value}
                                            name={`countsStats.${index}.value`}
                                        />
                                        </Box>
                                        <Box mr={2} flexBasis="33%">
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="Time Recorded"
                                            onBlur={handleBlur}
                                            onChange={(e) => setFieldValue(`countsStats.${index}.timeRecorded`, e.target.value)}
                                            value={values.countsStats[index].timeRecorded}
                                            name={`countsStats.${index}.timeRecorded`}
                                        />
                                        </Box>
                                        <Button variant="outlined" color="secondary" onClick={() => remove(index)}>Delete</Button>
                                    </Box>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => push({ name: "", value: 0, timeRecorded: "" })}
                                        >
                                        <Box component="span" fontSize="20px">+</Box> Add Count/Stat
                                    </Button>
                                </div>
                                )}
                            </FieldArray>
                            </Box>

                        <Box display="flex" justifyContent="end" mt="20px">
                            <Button type="submit" color="secondary" variant="contained">
                                Create New Data Asset
                            </Button>
                        </Box>
                    </Box>
                </form>
            )}
        </Formik>
    </Box>
    );
}

export default DataAssetForm
